import React, { useEffect, useState } from 'react';
import Head from 'next/head';
import config from '../lib/config';
import client from '../lib/apolloClient';
import Container from '../components/container';
import NavBar from '../components/navbar';
import Layout from '../components/layout';
import SectionSeparator from '../components/section-separator';
import PostPreview from '../components/post-preview';

import { GetStaticProps } from 'next';
import { useDebounce } from 'use-debounce';
import { gql, useLazyQuery } from '@apollo/client';
import { getPrimaryMenuItems } from '../lib/api';
import { Button } from '../components/button';
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { H2, H3, Label, Muted, Text } from '../components/text';
import { ISponsor } from '../models/sponsor';
import { IMenuItem } from '../models/menu-item';
import { IPostDirect } from '../models/post';

interface IProps {
	initialData: IPostDirect[];
	primaryMenuItems: IMenuItem[];
	sponsors: ISponsor[];
}

type SearchPostsType = { posts: { nodes: IPostDirect[] } };

const SearchPage = ({ initialData, primaryMenuItems, sponsors }: IProps) => {
	const [searchTerm, setSearchTerm] = useState('');
	const [searchPosts, { loading, data }] = useLazyQuery<SearchPostsType>(SEARCH_POSTS);

	const [debouncedValue] = useDebounce(searchTerm, 500);

	useEffect(() => {
		if (debouncedValue.length > 2) {
			onSearch();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [debouncedValue]);

	const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
		const value = event.target.value;
		setSearchTerm(value);
	};

	const onSearch = () => {
		searchPosts({
			variables: {
				search: debouncedValue,
			},
		});
	};

	const posts = data ? data.posts.nodes : initialData;
	const maxTrendingPosts = Math.min(initialData.length, 6);
	const trendingPosts = initialData.slice(0, maxTrendingPosts);
	const pageTitle = `Zoekpagina | ${config.siteName}`;
	const meta = 'Zoekpagina';

	return (
		<Layout preview sponsors={sponsors} metaTitle={pageTitle} metaDesc={meta}>
			<Head>
				<title>{pageTitle}</title>
			</Head>
			<NavBar items={primaryMenuItems} />
			<Container className="py-12 xl:py-16">
				<article className="mx-auto max-w-5xl">
					<div className="text-center flex flex-col items-center">
						<div className="flex items-center mb-4">
							<H2>Zoeken</H2>
							<MagnifyingGlassIcon className="mx-4 h-12 w-12" aria-hidden="true" />
						</div>
						<Muted>Gebruik het onderstaande zoekveld om te zoeken naar artikelen.</Muted>
					</div>
					<div className="mb-12">
						<form action="#" className="mt-6 flex">
							<label htmlFor="email" className="sr-only">
								Email address
							</label>
							<input
								type="search"
								className="px-4 font-inter block w-full rounded-md border-0 py-1.5 text-gray-900 ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
								placeholder="Zoek in alle artikelen, bv. naar je favoriete club"
								value={searchTerm}
								onChange={handleInputChange}
								autoFocus
							/>
							<Button type="submit" label="Zoeken" onClick={onSearch} className="ml-4 flex-shrink-0 " />
						</form>

						<SectionSeparator />

						{loading && (
							<div className="text-center p-6 min-h-[150px]">
								<Label className="block mt-2 text-base font-semibold text-black-300 mb-2">
									Bezig met zoeken...
								</Label>
							</div>
						)}

						{debouncedValue.length > 0 && posts.length === 0 && (
							<div className="text-center pb-6 min-h-[150px]">
								<Label className="block mt-2 text-base font-semibold text-black-300 mb-2">
									Geen artikelen gevonden
								</Label>
								<Text className="font-inter block mt-1 text-sm text-black-100">
									Probeer opnieuw te zoeken of begin alvast hieronder
								</Text>
							</div>
						)}

						{debouncedValue.length > 0 && posts.length > 0 && !loading && (
							<>
								<div className="mb-12">
									<H3 className="text-center mb-6 uppercase">Gevonden resultaten</H3>
									<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
										{!loading &&
											posts.map((post) => (
												<PostPreview
													key={post.slug}
													title={post.title}
													coverImage={post.featuredImage}
													date={post.date}
													slug={post.slug}
													excerpt={post.excerpt}
													bgColor="solid"
												/>
											))}
									</div>
								</div>
								<SectionSeparator />
							</>
						)}

						<div className="mb-12">
							<H3 className="text-center mb-6 uppercase">Trending</H3>
							<div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
								{trendingPosts.map((post) => (
									<PostPreview
										key={post.slug}
										title={post.title}
										coverImage={post.featuredImage}
										date={post.date}
										slug={post.slug}
										excerpt={post.excerpt}
										bgColor="solid"
									/>
								))}
							</div>
						</div>
					</div>
				</article>
			</Container>
		</Layout>
	);
};

const SEARCH_POSTS = gql`
	query SearchPosts($search: String!) {
		posts(first: 12, where: { search: $search }) {
			nodes {
				id
				date
				excerpt
				featuredImage {
					node {
						sourceUrl
					}
				}
				slug
				title
			}
		}
		sponsors(first: 20) {
			nodes {
				title
				sponsor_fields {
					subtitle
					websiteUrl
					mainSponsor
					invertedImage {
						sourceUrl
					}
				}
			}
		}
	}
`;

export const getStaticProps: GetStaticProps = async ({ preview = false }) => {
	const primaryMenuItems = await getPrimaryMenuItems();

	const { data } = await client.query({
		query: SEARCH_POSTS,
		variables: { search: null || '' },
	});

	return {
		revalidate: 60,
		props: {
			initialData: data.posts.nodes,
			sponsors: data.sponsors.nodes,
			preview,
			primaryMenuItems,
		},
	};
};

export default SearchPage;
